<template>
  <c-box
    id="quick-search-wrapper"
    :class="isComplex ? 'variation-complex-menuless' : 'variation-simple'"
    class="quick_solution_search_field"
  >
    <c-box width="100%" max-width="580px" overflow="auto" bg="none">
      <c-button
        v-if="isComplex"
        background-color="brand.purple"
        float="right"
        ml="md"
        @click.prevent.stop="openSearchPage()"
        :_hover="{ backgroundColor: 'brand.purpleLight' }"
      >
        <c-icon name="search" size="1.5rem" color="white" />
      </c-button>

      <c-box class="left-container" overflow="auto">
        <c-box class="term" overflow="auto">
          <c-input
            id="searchTerm"
            ref="term"
            v-model.trim="searchTerm"
            type="text"
            autocomplete="off"
            position="relative"
            px="0"
            mb="0.75rem"
            border-radius="0"
            border-width="0"
            border-bottom-width="1px"
            border-color="transparent"
            border-bottom-color="brand.purple"
            focus-border-color="transparent"
            background="transparent"
            :placeholder="placeholder || $t('solutions.quickSearch.placeholder')"
            @keydown.up.prevent.stop="selectPrevByKeyboard()"
            @keydown.down.prevent.stop="selectNextByKeyboard()"
            @keydown.enter="enterPressed()"
            @keydown.esc="escPressed()"
            @blur="hideResults()"
            @focus="
              showResults();
              $event.target.select();
            "
            :_hover="{ borderBottomColor: 'brand.purple' }"
            :_focus="{ borderBottomColor: 'brand.purple' }"
            :_active="{ borderBottomColor: 'brand.purple' }"
            v-chakra="
              (theme) => ({
                '&::placeholder': {
                  color: 'brand.purple',
                },
              })
            "
          />
          <c-box
            v-if="resultsVisible && searchResults"
            class="results-wrapper"
            position="absolute"
            width="80%"
            max-width="500px"
          >
            <c-box
              class="results"
              font-size="sm"
              position="relative"
              background="white"
              z-index="10"
              border="1px solid"
              border-color="brand.gray"
              border-radius="input"
              p="md"
              box-shadow="base"
            >
              <c-box
                v-for="(article, index) in searchResults.value"
                :id="`article-${index}`"
                :key="article.freshdeskId"
                class="item"
                :bg="index === selectedIndex ? 'brand.blueLightest' : 'white'"
                @click.prevent.stop="pickSolution(article)"
                cursor="pointer"
                color="black"
                p="md"
                overflow="auto"
                line-height="1.1"
                :border-bottom="index === searchResults.value.length - 1 ? 'none' : '1px solid'"
                :border-color="
                  index === searchResults.value.length - 1 ? 'none' : 'brand.grayLight'
                "
                :_hover="{ background: 'brand.grayLight' }"
              >
                <c-icon
                  :name="isVideo(article) ? 'support_video' : 'support_doc'"
                  color="brand.purple"
                  fill="white"
                  mr="md"
                />
                <span class="title">{{ article.title }}</span>
                <c-text
                  as="span"
                  class="category"
                  font-size="9px"
                  text-transform="uppercase"
                  bg="brand.grayLight"
                  mx="5px"
                  py="4px"
                  px="12px"
                  border-radius="4px"
                  white-space="nowrap"
                  float="right"
                >
                  {{ getCategoryName(article) }}
                </c-text>
              </c-box>

              <c-box
                v-if="resultsOverflow()"
                class="results-note"
                py="md"
                px="4px"
                :class="selectedIndex < 0 ? 'selected' : ''"
              >
                {{ $t("solutions.quickSearch.displayingFirstOnes", { amount: viewMax }) }}
                <a
                  href="#"
                  @click.stop.prevent="openSearchPage()"
                  v-chakra
                  text-decoration="underline"
                >
                  {{ $t("solutions.quickSearch.displayAll", { count: searchResults.count }) }}
                </a>
              </c-box>
              <c-box v-if="searchResults.value.length === 0" class="results-note" py="md" px="4px">
                {{ $t("solutions.quickSearch.noResults") }}
              </c-box>
            </c-box>
          </c-box>
          <c-box
            v-else-if="resultsVisible && searchTerm.trim().length"
            class="results-wrapper"
            position="absolute"
            width="80%"
            max-width="500px"
          >
            <c-box
              class="results"
              font-size="sm"
              position="relative"
              background="white"
              z-index="10"
              border="1px solid"
              border-color="brand.gray"
              border-radius="input"
              p="md"
              box-shadow="base"
            >
              <c-box class="results-note" py="md" px="4px">
                {{ $t("solutions.quickSearch.searching") }}
              </c-box>
            </c-box>
          </c-box>
        </c-box>
      </c-box>
    </c-box>
  </c-box>
</template>

<script lang="ts">
import { ISolutionsState } from "@/store/solutionsModule";
import { getCategoryName, getFolderName, isVideo } from "@/util/SolutionUtil";
import { isElementInViewport } from "@/util/ViewportUtil";
import { CBox, CButton, CIcon, CInput, CText } from "@chakra-ui/vue";
import {
  ISolutionArticle,
  ISolutionArticleSearchResult,
  ISolutionCategoryStructure,
} from "@iloq/shared";
import _ from "lodash";
import { defineComponent } from "vue";
import { mapState } from "vuex";

export default defineComponent({
  name: "QuickSolutionSearch",
  components: {
    CBox,
    CInput,
    CButton,
    CIcon,
    CText,
  },
  props: {
    value: {
      type: String,
      default: "",
    },
    inputClass: {
      type: String,
      default: undefined,
    },
    placeholder: {
      type: String,
      default: undefined,
    },
    isComplex: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapState("solutions", {
      structure: (state: unknown) => (state as ISolutionsState).structure,
    }),
  },
  data() {
    return {
      searchTerm: "",
      lastSearchTerm: "",
      typing: false,
      searchResults: null as ISolutionArticleSearchResult | null,
      selectedIndex: -1,
      resultsVisible: true,
      viewMax: 8,
      selectedCategory: null as ISolutionCategoryStructure | null,
      lastSearchCategory: null as ISolutionCategoryStructure | null,
    };
  },
  directives: {
    "click-outside": {
      bind(element, binding, vnode) {
        const el = element as any;
        el.clickOutsideEvent = (event: any) => {
          if (!(el === event.target || el.contains(event.target))) {
            if (vnode && vnode.context) {
              (vnode.context as any)[binding.expression](event);
            }
          }
        };
        document.body.addEventListener("click", el.clickOutsideEvent);
      },
      unbind(element) {
        const el = element as any;
        document.body.removeEventListener("click", el.clickOutsideEvent);
      },
    },
  },
  watch: {
    searchTerm: "onSearchParametersUpdated",
    selectedCategory: "onSearchParametersUpdated",
  },
  methods: {
    isVideo(article: ISolutionArticle): boolean {
      return isVideo(article);
    },
    onSearchParametersUpdated(): void {
      this.ensureSearchFieldIsVisible();
      if (
        this.searchTerm.trim() !== (this.lastSearchTerm || "").trim() ||
        this.selectedCategory !== this.lastSearchCategory
      ) {
        this.typing = true;
        this.resultsVisible = true;
        this.searchResults = null;
        this.search(this.searchTerm, this.selectedCategory);
      }
    },
    search: _.debounce(function (
      this: any,
      term: string,
      category: ISolutionCategoryStructure | null
    ): void {
      this.ensureSearchFieldIsVisible(true);
      if (term !== this.lastSearchTerm || category !== this.lastSearchCategory) {
        this.lastSearchTerm = term;
        this.lastSearchCategory = category;
        if (term.trim().length > 0) {
          this.$store
            .dispatch("solutions/searchArticlesQuick", {
              term,
              categoryId: category ? category.id : undefined,
              top: this.viewMax,
            })
            .then((results: ISolutionArticleSearchResult) => {
              if (term === this.lastSearchTerm && category === this.lastSearchCategory) {
                this.searchResults = results;
                this.selectedIndex = -1;
              }
            });
        } else {
          this.searchResults = null;
        }

        this.typing = false;
      }
    },
    400),
    resultsOverflow(): boolean {
      if (this.searchResults) {
        return this.searchResults.value.length < this.searchResults.count;
      }
      return false;
    },
    pickSolution(a: ISolutionArticle): void {
      this.$store.commit("solutions/setLastPicked", a);
      this.$router.push({
        path: `/support/solutions/categories/${a.categoryId}/${a.folderId}/${a.freshdeskId}`,
      });
      this.clearSearch();
    },
    selectPrevByKeyboard(): void {
      if (this.searchResults) {
        this.selectedIndex -= 1;
        if (this.selectedIndex < (this.resultsOverflow() ? -1 : 0)) {
          this.selectedIndex = this.searchResults.value.length - 1;
        }
        this.ensureSelectedIsVisible();
      }
    },
    selectNextByKeyboard(): void {
      if (this.searchResults) {
        this.selectedIndex += 1;
        if (this.selectedIndex >= this.searchResults.value.length) {
          this.selectedIndex = this.resultsOverflow() ? -1 : 0;
        }
        this.ensureSelectedIsVisible();
      }
    },
    ensureSelectedIsVisible(): void {
      if (!this.searchResults) {
        return;
      }

      const visibleElements: any[] = [];
      if (this.selectedIndex === 0) {
        visibleElements.push(document.getElementById("searchTerm"));
      } else {
        visibleElements.push(document.getElementById(`article-${this.selectedIndex - 1}`));
      }
      if (this.selectedIndex < this.searchResults.value.length - 1) {
        visibleElements.push(document.getElementById(`article-${this.selectedIndex + 1}`));
      }

      visibleElements.forEach((el: any) => {
        if (el) {
          el.scrollIntoView({
            behavior: "auto",
            block: "nearest",
          });
        }
      });
    },
    ensureSearchFieldIsVisible(smooth = false): void {
      const el = document.getElementById("quick-search-wrapper");
      if (el && !isElementInViewport(el)) {
        el.scrollIntoView({
          behavior: smooth ? "smooth" : "auto",
          block: "start",
        });
      }
    },
    enterPressed(): void {
      if (this.resultsVisible) {
        this.pickSolutionByKeyboard();
      } else {
        this.resultsVisible = true;
      }
    },
    escPressed(): void {
      if (this.selectedIndex >= 0) {
        this.selectedIndex = -1;
      } else {
        this.clearSearch();
      }
      this.ensureSearchFieldIsVisible();
    },
    pickSolutionByKeyboard(): void {
      if (this.searchResults) {
        if (this.selectedIndex >= 0) {
          this.pickSolution(this.searchResults.value[this.selectedIndex]);
        } else {
          this.openSearchPage();
        }
      }
    },
    hideResults(): void {
      setTimeout(() => {
        this.resultsVisible = false;
      }, 300);
    },
    showResults(): void {
      this.resultsVisible = true;
    },
    clearSearch(): void {
      this.searchTerm = "";
    },
    getCategoryName(article: ISolutionArticle): string {
      return getCategoryName(this.structure, article.categoryId, article.categoryName);
    },
    getFolderName(article: ISolutionArticle): string {
      return getFolderName(this.structure, article.folderId, article.folderName);
    },
    openSearchPage(): void {
      const term = `?term=${encodeURIComponent(this.searchTerm)}`;
      const category = this.selectedCategory ? `&category=${this.selectedCategory.id}` : "";
      this.$router.push({ path: `/support/solutions/search${term}${category}` });
    },
  },
});
</script>
